import { Close } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import FileIcon from 'assets/img/file4.svg';
import FolderIcon from 'assets/img/folder.svg';
import { saveAs } from 'file-saver';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { GetDocumentLink, UpdateMerchantDocument } from 'store/actions/complianceActions';
import { getFile } from 'store/actions/editorActions';
import { extractDocName } from '../../utils';
import PolicyDefaultDrawer from '../PolicyEditorDrawer/NavBar/PolicyDefaultDrawer';

export default function UploadFromComputerTab({
    isOpen,
    handleCallback,
    standard,
    templateId,
    existingPolicyId,
    template,
    templateUniqueId,
    isPolicyDefaultDrawerOpen,
    setIsPolicyDefaultDrawerOpen,
}) {
    const [formData, setFormData] = useState({
        document: null,
    });
    const [formErrors, setFormErrors] = useState({
        document: '',
    });
    const [isLoading, setIsLoading] = useState(false);

    const user_id = useSelector((state) => state?.authReducers?.user_id);
    const all_merchant_documents = useSelector((state) => state?.complianceReducers?.all_merchant_documents);

    const dispatch = useDispatch();

    const fileInputRef = useRef(null);

    const { pdf, docx, excel, csv, ppt, txt } = {
        pdf: '.pdf',
        docx: '.docx, .doc',
        excel: '.xlsx, .xls',
        csv: '.csv',
        ppt: '.pptx, .ppt',
        txt: '.txt',
    };

    const allDocuments = useMemo(
        () => all_merchant_documents?.map((doc) => ({ ...doc, default: doc?.document })),
        [all_merchant_documents]
    );

    const standardNeedsApproval = useMemo(
        () => standard === 'soc2' || standard === 'sslc' || standard === 'iso9001' || standard === 'iso20000',
        [standard]
    );

    const currentDocument = useMemo(
        () => (existingPolicyId ? allDocuments?.find((doc) => doc?.id === existingPolicyId) : null),
        [allDocuments, existingPolicyId]
    );

    const dragEnter = () => {
        const target = document.getElementById('drop-zone');
        target.style.borderColor = '#C4C4C4';
        target.style.backgroundColor = '#A58980';
    };

    const dragLeave = () => {
        const target = document.getElementById('drop-zone');
        target.style.borderColor = '#A58980';
        target.style.backgroundColor = 'transparent';
    };

    const dragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file && `${pdf},${docx},${excel},${csv},${ppt},${txt}`.includes(file.type)) {
            const reader = new FileReader();
            reader.onload = () => {
                setFormData((prev) => ({
                    ...prev,
                    document: reader.result,
                }));
                setFormErrors((prev) => ({
                    ...prev,
                    document: '',
                }));
            };
            reader.readAsDataURL(file);
            // convertedBase64(file).then(() => {});
        } else {
            // enqueueSnackbar('Invalid Data Dropped', { variant: 'error' });
            setFormErrors((prev) => ({
                ...prev,
                document: 'Invalid file type',
            }));
        }
        dragLeave();
    };

    const uploadDoc = async (event) => {
        const input = event.target;
        const file = input.files?.item(0);
        if (!file) return;
        const fileSize = file?.size / 1000 / 1000;
        if (fileSize > 5) {
            toast.info('File too large, minimum of 5MB');
            setFormData((prev) => ({
                ...prev,
                document: null,
            }));
        } else {
            // const base64 = await convertedBase64(file);
            setFormData((prev) => ({
                ...prev,
                document: file,
            }));
            setFormErrors((prev) => ({
                ...prev,
                document: '',
            }));
        }
    };

    function validateForm() {
        const errors = {
            document: '',
        };

        let isValid = true;

        if (!formData.document) {
            errors.document = 'Document is required';
            isValid = false;
        }

        setFormErrors(errors);

        return isValid;
    }

    function resetForm() {
        setFormData({
            document: null,
        });

        setFormErrors({
            document: '',
        });
    }

    async function uploadDocumentChanges() {
        if (!validateForm()) {
            return;
        }

        try {
            setIsLoading(true);

            const nativeFormData = {
                existing_policy: existingPolicyId,
                document_id: templateId,
                ...(templateUniqueId && {
                    [standard === 'blockchain' ? 'subcriteria' : 'cii_subsector']: templateUniqueId,
                }),
                document: formData?.document,
                filename: formData?.document?.name,
                is_editor: false,
                tag: standard,
                requested_by: user_id,
            };

            const response = await dispatch(UpdateMerchantDocument(nativeFormData, existingPolicyId, standard));
            if (!response.success) throw new Error(response?.message);

            toast.success('Document updated successfully');

            handleClose();
        } catch (error) {
            toast.error(error?.message || 'Failed to save document');
        } finally {
            setIsLoading(false);
        }
    }

    function handleSubmit() {
        if (!validateForm()) {
            return;
        }

        setIsPolicyDefaultDrawerOpen(true);
    }

    function handleClose() {
        resetForm();
        handleCallback();
    }

    async function getDocumentLink() {
        try {
            setIsLoading(true);

            const authorizedUrlResponse = await dispatch(GetDocumentLink('document', templateId));
            if (!authorizedUrlResponse?.success) {
                throw new Error('Failed to get authorized document link');
            }

            const fileResponse = await dispatch(getFile(authorizedUrlResponse?.data));
            if (!fileResponse?.success) {
                throw new Error(fileResponse?.message);
            }

            saveAs(fileResponse?.blob, extractDocName(template?.default)?.replaceAll('_', ' '));
        } catch (error) {
            toast.error(error?.message || 'Something went wrong!');
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (isOpen) {
            resetForm();
        }
    }, [isOpen]);

    return (
        <>
            <div className="!h-full flex flex-col !gap-4 justify-between">
                <div className="flex flex-col !gap-4 !py-4 !px-6">
                    <div className="bg-[#F8FAFC] !border !border-[#F0F0FA] !rounded flex !gap-1 !px-4 !py-2 items-start">
                        <img src="/img/frameworks/lightbulb.svg" alt="Bulb Icon" className="!mt-0.5 !w-6 !h-6" />

                        <p className="text-[#395BA9] font-normal text-[13px] leading-5">
                            For a smoother experience and faster auditing, we recommend using the template editor.
                        </p>
                    </div>

                    <div className="flex flex-col !gap-2">
                        <Box
                            sx={{
                                border: '1px dashed #CBD5E1',
                                borderRadius: '2px',
                                minHeight: 150,
                                position: 'relative',
                                display: 'grid',
                                placeItems: 'center',
                                cursor: 'pointer',
                                '&:hover': {
                                    borderColor: '#64748B',
                                    backgroundColor: '#F8FAFC',
                                },
                            }}
                            onClick={() => fileInputRef?.current?.click()}
                        >
                            <Box
                                id="drop-zone"
                                onDragEnter={dragEnter}
                                onDragLeave={dragLeave}
                                onDrop={handleDrop}
                                onDragOver={dragOver}
                                sx={{ p: 2 }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <img src={FolderIcon} alt="Folder Icon" />
                                    <Typography sx={{ color: '#64748B', fontSize: '12px', fontWeight: 500 }}>
                                        Drop your files or click to {existingPolicyId ? 'update' : 'upload'}
                                    </Typography>
                                    <Typography sx={{ color: '#94A3B8', fontSize: '12px', fontWeight: 400 }}>
                                        Supported file types: PDF, DOCX, XLSX, CSV, PPTX, TXT
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1 }}>
                                    <label htmlFor="">
                                        <Box
                                            sx={{
                                                border: '1px solid #E2E8F0',
                                                cursor: 'pointer',
                                                padding: 0.5,
                                            }}
                                        >
                                            <Typography sx={{ color: '#475569', fontWeight: 400, fontSize: 12 }}>
                                                Browse
                                            </Typography>
                                        </Box>
                                    </label>
                                    <input
                                        type="file"
                                        accept={`${pdf},${docx},${excel},${csv},${ppt},${txt}`}
                                        onClick={(e) => (e.target.value = null)}
                                        onChange={(e) => uploadDoc(e)}
                                        hidden
                                        id=""
                                        ref={fileInputRef}
                                    />
                                </Box>
                            </Box>
                        </Box>

                        {formErrors?.document && (
                            <span className="text-[10px] !-mt-1 text-[#FF0000] font-normal">
                                {formErrors?.document}
                            </span>
                        )}

                        {!existingPolicyId && (
                            <p className="text-[13px] text-[#64748B]">
                                We recommend using file names that clearly describe the content.
                            </p>
                        )}

                        {formData.document && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <img src={FileIcon} alt="icon" />

                                <Box sx={{ ml: 1 }}>
                                    <Typography
                                        sx={{
                                            color: '#475569',
                                            fontWeight: 400,
                                            fontSize: '14px',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {formData.document.name}
                                    </Typography>
                                </Box>

                                <button
                                    className="text-red-500 ml-auto !border !border-[#E2E8F0] !h-5 !w-5 !rounded-full grid place-items-center transition-all hover:brightness-90 bg-[#fff]"
                                    onClick={() =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            document: null,
                                        }))
                                    }
                                >
                                    <Close sx={{ width: 13, height: 13 }} />
                                </button>
                            </Box>
                        )}
                    </div>

                    <div className="flex items-center !gap-2">
                        <img src="/img/frameworks/file-download.svg" alt="file download" className="!w-5 !h-5" />

                        <p className="text-[#475569] font-semibold text-sm">
                            Don't have a template for your policy?.{' '}
                            <span
                                className="text-[#395BA9] decoration-[#395BA9] underline font-bold cursor-pointer transition-all hover:no-underline disabled:opacity-50 disabled:pointer-events-none"
                                role="button"
                                disabled={isLoading}
                                onClick={() => getDocumentLink()}
                            >
                                {isLoading ? 'Downloading...' : 'Download our template'}
                            </span>
                        </p>
                    </div>
                </div>

                <div className="flex items-center justify-end !p-6 bg-[#F8FAFC] !gap-2">
                    <button
                        className="bg-[#fff] !border !border-[#E2E8F0] !rounded-sm !px-4 !py-2 hover:bg-red-500 hover:text-white transition-colors text-[#334155] text-sm font-medium"
                        onClick={handleClose}
                    >
                        Cancel
                    </button>

                    <button
                        className="bg-[#202D66] !rounded-sm !px-4 !py-2 hover:bg-[#151e44] transition-all text-white text-sm font-semibold disabled:opacity-50 disabled:pointer-events-none flex items-center !gap-1"
                        disabled={isLoading}
                        onClick={() => {
                            if (standardNeedsApproval && currentDocument?.approved_status === 'pending') {
                                uploadDocumentChanges();
                            } else {
                                handleSubmit();
                            }
                        }}
                    >
                        {isLoading && <Loader2 className="!w-5 !h-5 animate-spin" />}
                        {standardNeedsApproval && currentDocument?.approved_status === 'pending'
                            ? 'Update'
                            : 'Continue'}
                    </button>
                </div>
            </div>

            <PolicyDefaultDrawer
                isOpen={isPolicyDefaultDrawerOpen}
                setIsOpen={setIsPolicyDefaultDrawerOpen}
                handleCallback={() => {
                    handleClose();
                }}
                document={formData?.document}
                standard={standard}
                templateId={templateId}
                templateUniqueId={templateUniqueId}
                existingPolicyId={existingPolicyId}
                filename={formData?.document?.name}
            />
        </>
    );
}
